/**
 * Ctext Component
 * Granular text component with theme-based styling
 */
'use client';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { ColorType, getColorValue } from '@/theme/colors';
import { FontWeight, getFontFamily } from '@/theme/typography';
import { pxToRem } from '@/theme/spacing';
import { TextPreset } from '@/constants/componentPresets';
const CTEXT_ALIGN_LEFT = 'left';
const CTEXT_LOADING_PRESET_ITERATE_FADE = 'iterate-fade';
const CTEXT_ITERATE_FADE_SPACE_CHARACTER = ' ';
const CTEXT_ITERATE_FADE_SPACE_KEY_TOKEN = 'space';
const CTEXT_ITERATE_FADE_CHARACTER_POSITION_SEPARATOR = '-position-';
export interface CtextProps {
    children: ReactNode;
    size?: number;
    weight?: FontWeight;
    color?: ColorType;
    opacity?: number;
    preset?: TextPreset;
    align?: 'left' | 'center' | 'right';
    highlight?: boolean; // If true, @mentions are highlighted in accent color
    loadingPreset?: 'iterate-fade'; // Loading animation effects
    limit?: number; // If provided, truncate text to this many characters and add ".."
    smooth?: boolean; // If true, apply CSS transitions for color and font-weight changes
    className?: string;
    style?: CSSProperties;
    suppressHydrationWarning?: boolean;
}
interface PresetConfig {
    size: number;
    weight: FontWeight;
    color: ColorType;
    opacity?: number;
}
const presetConfigs: Record<TextPreset, PresetConfig> = {
    [TextPreset.Regular]: {
        size: 17,
        weight: FontWeight.Medium,
        color: ColorType.White,
        opacity: 1,
    },
    [TextPreset.Strong]: {
        size: 20,
        weight: FontWeight.Heavy,
        color: ColorType.White,
        opacity: 1,
    },
    [TextPreset.Mild]: {
        size: 17,
        weight: FontWeight.Medium,
        color: ColorType.White,
        opacity: 0.65,
    },
    [TextPreset.Accent]: {
        size: 18,
        weight: FontWeight.Medium,
        color: ColorType.Accent,
        opacity: 1,
    },
    [TextPreset.ChipVersion]: {
        size: 15,
        weight: FontWeight.Medium,
        color: ColorType.White,
        opacity: 0.7,
    },
    [TextPreset.ChipDent]: {
        size: 14.4,
        weight: FontWeight.Regular,
        color: ColorType.White,
        opacity: 0.7,
    },
};
export function Ctext({ children, size, weight, color, opacity, preset = TextPreset.Mild, align = CTEXT_ALIGN_LEFT, highlight = true, loadingPreset, limit, smooth = false, className, style, suppressHydrationWarning, }: CtextProps) {
    const presetConfig = presetConfigs[preset];
    // Merge preset with explicit props (explicit props override preset)
    const finalSize = size ?? presetConfig.size;
    const finalWeight = weight ?? presetConfig.weight;
    const finalColor = color ?? presetConfig.color;
    const finalOpacity = opacity ?? presetConfig.opacity;
    const textStyle: CSSProperties = useMemo(() => ({
        fontFamily: getFontFamily(),
        fontSize: pxToRem(finalSize),
        fontWeight: finalWeight,
        color: getColorValue(finalColor, finalOpacity),
        textAlign: align,
        margin: 0,
        padding: 0,
        lineHeight: 1.5,
        overflowWrap: 'break-word',
        // Smooth transitions for color and font-weight changes (e.g., highlight on update)
        transition: smooth ? 'color 0.3s ease, font-weight 0.3s ease' : undefined,
        ...style,
    }), [finalSize, finalWeight, finalColor, finalOpacity, align, smooth, style]);
    // Parse and highlight @mentions if highlight is enabled and children is a string
    const renderedContent = useMemo(() => {
        // Apply character limit if specified and children is a string
        let processedText = children;
        if (limit !== undefined && typeof children === 'string') {
            if (children.length > limit) {
                processedText = children.slice(0, limit) + '..';
            }
        }
        // Loading preset: iterate-fade animation (split into characters)
        if (loadingPreset === CTEXT_LOADING_PRESET_ITERATE_FADE && typeof processedText === 'string') {
            return (<span style={{
                    display: 'inline-block',
                    padding: '0 8px',
                    animation: 'ctext-iterate-fade-parent 1.5s infinite',
                }}>
          {processedText.split('').map((char, index) => {
                    const characterKey = `${char === CTEXT_ITERATE_FADE_SPACE_CHARACTER
                        ? CTEXT_ITERATE_FADE_SPACE_KEY_TOKEN
                        : char}${CTEXT_ITERATE_FADE_CHARACTER_POSITION_SEPARATOR}${index + 1}`;
                    return (<span key={characterKey} style={{
                            display: 'inline-block',
                            animationName: 'ctext-iterate-fade-child',
                            animationDuration: '1.5s',
                            animationIterationCount: 'infinite',
                            animationDelay: `${index * 0.05}s`,
                        }}>
                {char === CTEXT_ITERATE_FADE_SPACE_CHARACTER ? '\u00A0' : char}
              </span>);
                })}
        </span>);
        }
        if (!highlight || typeof processedText !== 'string') {
            return processedText;
        }
        // Split text by @mentions (@ followed by word characters and hyphens only)
        // Stops at punctuation like commas, periods, colons, etc.
        const parts = processedText.split(/(@[\w-]+)/g);
        return parts.map((part, index) => {
            // Check if this part is an @mention
            if (part.startsWith('@')) {
                return (<span key={index} style={{
                        color: getColorValue(ColorType.Accent, 1),
                    }}>
            {part}
          </span>);
            }
            return part;
        });
    }, [children, highlight, loadingPreset, limit]);
    return (
      <span className={className} style={textStyle} suppressHydrationWarning={suppressHydrationWarning}>
        {renderedContent}
      </span>
    );
}
